import React from "react";
import {
  IconName,
  RasterIconName,
  SvgIconName,
  rasterIconRegistry,
  svgIconRegistry,
} from "../assets/icons/registry";

export type { IconName } from "../assets/icons/registry";

export interface IconProps
  extends Omit<React.SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  /** Rendered size in pixels. Defaults to the asset's 14px, 16px, 24px, or 28px size. */
  size?: number;
}

const getNativeSize = (name: IconName): 14 | 16 | 24 | 28 =>
  name.endsWith("-14") ? 14 : name.endsWith("-16") ? 16 : name.endsWith("-28") ? 28 : 24;

/**
 * View R Studio icon renderer.
 *
 * SVG assets render inline so currentColor follows the owning control's CSS
 * color. The single bitmap-only ProtoPie mark is wrapped in the same SVG box
 * so every icon keeps one sizing and accessibility contract.
 */
const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, size, style, ...props },
  ref,
) {
  const nativeSize = getNativeSize(name);
  const renderedSize = size ?? nativeSize;
  const isLabelled = Boolean(
    props["aria-label"] || props["aria-labelledby"],
  );
  const ariaHidden = props["aria-hidden"] ?? (isLabelled ? undefined : true);
  const sharedProps = {
    ...props,
    ref,
    width: renderedSize,
    height: renderedSize,
    "aria-hidden": ariaHidden,
    style: {
      display: "block",
      flexShrink: 0,
      ...style,
    },
  };

  if (name in rasterIconRegistry) {
    const source = rasterIconRegistry[name as RasterIconName];
    return (
      <svg viewBox={`0 0 ${nativeSize} ${nativeSize}`} {...sharedProps}>
        <image
          href={source}
          width={nativeSize}
          height={nativeSize}
          preserveAspectRatio="xMidYMid meet"
        />
      </svg>
    );
  }

  const Svg = svgIconRegistry[name as SvgIconName];
  return <Svg {...sharedProps} />;
});

Icon.displayName = "Icon";

export default Icon;
